import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppStore } from '../../store/useAppStore';
import { Button } from '../ui/button';
import { FloatingApiKeyManager } from '../ui/FloatingApiKeyManager';
import { UserProfile } from '../Auth/UserProfile';
import { API_CONFIG } from '../../config/api';
import { 
  Menu, 
  Key
} from 'lucide-react';

export const Header: React.FC = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const { 
    setMobileSidebarOpen,
    apiKey,
    setApiKey,
    isKeyRequired,
    setIsKeyRequired
  } = useAppStore();
  
  const [showApiKeyManager, setShowApiKeyManager] = useState(false);
  const [isCheckingRequirement, setIsCheckingRequirement] = useState(true);

  // Check if API key is required
  useEffect(() => {
    const checkKeyRequirement = async () => {
      if (!user) {
        setIsCheckingRequirement(false);
        setIsKeyRequired(true);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(API_CONFIG.endpoints.config.keyRequired, {
          headers: API_CONFIG.getAuthHeaders(token)
        });
        
        if (response.ok) {
          const data = await response.json();
          setIsKeyRequired(data.key_required);
        } else {
          setIsKeyRequired(true);
        }
      } catch (error) {
        console.error('Failed to check key requirement:', error);
        setIsKeyRequired(true);
      } finally {
        setIsCheckingRequirement(false);
      }
    };

    checkKeyRequirement();
  }, [user, getAccessTokenSilently, setIsKeyRequired]);

  const handleApiKeyUpdate = (key: string) => {
    setApiKey(key);
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4">
      {/* Left Section */}
      <div className="flex items-center gap-4">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMobileSidebarOpen(true)}
          className="lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </Button>
        
        {/* Page title can go here */}
        <h1 className="text-lg font-semibold text-gray-900">
          
        </h1>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2">
        {/* API Key Button */}
        <Button
          variant={apiKey ? "outline" : "default"}
          size="sm"
          onClick={() => setShowApiKeyManager(true)}
          className={!apiKey && isKeyRequired ? "animate-pulse" : ""}
        >
          <Key className="h-4 w-4 mr-2" />
          {apiKey ? "API Key Set" : "Add API Key"}
        </Button>

        {/* User Profile */}
        <UserProfile />
      </div>

      {/* API Key Manager Modal */}
      {showApiKeyManager && (
        <FloatingApiKeyManager
          value={apiKey}
          onChange={handleApiKeyUpdate}
          isRequired={isKeyRequired}
          isOpen={showApiKeyManager}
          onToggle={() => setShowApiKeyManager(false)}
        />
      )}
    </header>
  );
};